/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Paper, Stack, TextField, Button, Container, Box, Typography, Backdrop, CircularProgress } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { Check, Close, Edit, Error } from '@mui/icons-material';
import { useState, useEffect, Dispatch, SetStateAction, ChangeEvent, } from "react";

import { useAppBarHeight } from '../../../common/CustomHooks';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router-dom';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import { stripedDateGrid } from '../../../common/CustumEmotionStyles';
import { remToPixels } from '../../../common/CustomFunctions';
import DialogAddEditPeople from '../components/DialogAddEditPeople';
import PeopleBusinessLogic from '../../../businessLogic/PeopleBusinessLogic';
import { IPeopleGuiModel } from '../../../model/IPeopleGuiModel';

const textBoxInputStylePeople = (theme: MaterialTheme) => css({
    backgroundColor: theme.palette.background.paper,
    fontWeight: "bold",
    //padding: theme.spacing(1.5),
    ".MuiOutlinedInput-input": { fontSize: "1rem" },
})

const People = (): JSX.Element => {

    const { organizationId } = useParams();

    const theme = useTheme();

    const { t, i18n } = useTranslation();

    const [openDialog, setOpenDialog] = useState(false);
    const handleDialogOpen = () => {
        setOpenDialog(true)
    }
    const [openDatagridDialog, setOpenDatagridDialog] = useState(false);
    const handleDatagridDialogOpen = () => {
        setOpenDatagridDialog(true)
    }
    const handleDialogClose = () => {
        setOpenDialog(false)
        setOpenDatagridDialog(false);
    }

    const [people, setPeople] = useState<IPeopleGuiModel[]>([]);
    const [peropleErrors, setPeopleErrors] = useState<string[]>([]);

    const [currentId, setCurrentId] = useState<number>();
    const [currentFirstName, setCurrentFirstName] = useState("");
    const [currentLastName, setCurrentLastName] = useState("");
    const [currentUserId, setCurrentUserId] = useState("");
    const [currentIsActive, setCurrentIsActive] = useState(false);

    const handleDialogDatagridOpen = (id: number, oldFirstName: string, oldLastName: string, oldUserId: string, oldIsActive: boolean) => {
        setCurrentId(id);
        setCurrentFirstName(oldFirstName);
        setCurrentLastName(oldLastName);
        setCurrentUserId(oldUserId);
        setCurrentIsActive(oldIsActive);
        setOpenDatagridDialog(true);
    }

    const [limitPerPage, setLimitPerPage] = useState(0);
    const [totalItems, setTotalItems] = useState(0);

    const [loader, setLoader] = useState(false);

    //###############################################################################################################################################
    //########################################################API Calls and useEffects###############################################################
    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        //const service = new SourceDocumentService();

        getPeople(isMounted);

        return () => { isMounted = false };
    }, [organizationId]);

    const appBarHeight = useAppBarHeight();
    //########################################################################################
    //########################################################################################

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'id', width: 60, hide: true, },
        { field: 'fullName', headerName: t('basic.name'), width: 200, },
        {
            field: 'isActive', headerName: t('basic.isActive'), width: 200, renderCell: (param: GridCellParams) => {
                if (param.value) {
                    return <Check fontSize="large" color="success"/>
                } else {
                    return <Close fontSize="large" color="error"/>
                }
            }
        },
        {
            field: 'isMappedToUser', headerName: t('basic.isMappedToUser'), width: 200, renderCell: (param: GridCellParams) => {
                if (param.value) {
                    return <Check fontSize="large" color="success" />
                } else {
                    return <Close fontSize="large" color="error" />
                }
            }
        },
        {
            field: 'edit', headerName: "", align: "center", width: 100, renderCell: (param: GridCellParams) => (
                <Button
                    onClick={() => handleDialogDatagridOpen(parseInt(param.row.id), param.row.firstName, param.row.lastName, param.row.userId, param.row.isActive)}
                    variant="contained"
                    color="secondary">
                    {t("basic.edit")}
                </Button>
            )
        },
    ];

    function CustomErrorOverlay() {
        return <Grid container direction="column" justifyContent="center" alignItems="center" height="100%">
            <Grid><Error fontSize="large" /></Grid>
            <Grid width="15rem" display="flex" justifyContent="center">
                <Typography variant="body2">
                    {t("basic.unexpectedError")}
                </Typography>
            </Grid>
        </Grid>
    }
    function CustomToolbar() {
        return (
            <Stack direction="row" justifyContent="space-between" spacing={4} margin={theme.spacing(3)}>
                <Typography variant="h5">{t("basic.people")}</Typography>
                <Stack direction="row" spacing={4}>
                    <TextField
                        css={textBoxInputStylePeople(theme)}
                        size="small"
                        variant="outlined"
                        label={t("search.search")}
                        color="warning"
                        value={undefined}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => undefined} />
                    <Button variant="contained" onClick={handleDialogOpen}>
                        {t("basic.add")}
                    </Button>
                </Stack>
            </Stack>
        )
    }

    //###################################################################

    const getPeople = async (isMounted: boolean) => {

        if (organizationId) {
            const logic = new PeopleBusinessLogic()
            setLoader(true);
            const result = await logic.getPeople(organizationId);
            setLoader(false);

            if (result.hasErrors) {
                if (isMounted) {
                    toast(t("basic.errorData") + result.errors.join(", "))
                    setPeopleErrors(result.errors);
                }
            } else {
                if (isMounted) {
                    setPeopleErrors([]);
                    setPeople(result.value);
                }
            }
        }

    }

    //###################################################################

    return <main>
        <Box
            sx={{
                padding: theme.spacing(2),
                width: "100%",
                height: {
                    xs: "calc(100vh - " + appBarHeight + "px )",
                    xl: "calc(100vh)"
                },
                //"& .MuiDataGrid-columnHeaders": { backgroundColor: theme.palette.info.main, color: "white" },
                //"& .MuiDataGrid-footerContainer": { backgroundColor: theme.palette.info.main, color: "white" }
            }}>
            {/*<Grid container spacing={2} padding={theme.spacing(2)}>*/}

            {/*</Grid>*/}
            <Container maxWidth="md" disableGutters sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <Paper sx={{ height: "100%" }} >
                    <DataGrid css={stripedDateGrid(theme, theme.palette.grey[200])}
                        rows={people}
                        columns={columns}
                        rowHeight={remToPixels(2)}
                        //pagination
                        //paginationMode="server"
                        //pageSize={limitPerPage}
                        //rowCount={totalItems}
                        //loading={loader}
                        //onPageChange={(newPage) => {
                        //    changePage(newPage);
                        //}}
                        headerHeight={remToPixels(4)}
                        getRowClassName={(params) =>
                            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                        }
                        error={peropleErrors.length > 0 ? true : undefined}
                        components={{
                            ErrorOverlay: CustomErrorOverlay,
                            Toolbar: CustomToolbar,
                        }} />
                </Paper>
            </Container>
            <DialogAddEditPeople
                open={openDialog}
                closeDialog={() => setOpenDialog(false)}
                organizationId={organizationId ? organizationId : ""}
                postOrPut={'post'}
                getPeople={() => getPeople(true)} />
            <DialogAddEditPeople open={openDatagridDialog}
                closeDialog={handleDialogClose}
                organizationId={organizationId ? organizationId : ""}
                postOrPut={'put'}
                id={currentId}
                oldFirstName={currentFirstName}
                oldLastName={currentLastName}
                oldUserId={currentUserId}
                oldIsActive={currentIsActive}
                getPeople={() => getPeople(true)} />
        </Box>
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loader}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    </main>

}
export default People;